import type { Scan } from '../types/scan'
import { getFallbackRecommendations } from '../data/recommendations'

export type RiskLevel = 'low' | 'medium' | 'high'
export type Severity = 'HIGH' | 'MEDIUM' | 'LOW'

export interface NormalizedPattern {
  type: string
  severity: Severity
  /** Backend description — conversation-specific, paid report only. */
  description: string | null
  /** Real evidence-signal count, or null when the backend didn't provide one. */
  signalCount: number | null
  /** Exact evidence lines — paid report only. Never contains the LOCKED sentinel. */
  examples: string[]
  /** True when the backend withheld this pattern's evidence (example === "LOCKED"). */
  evidenceLocked: boolean
}

/** Sentinel the backend writes in place of evidence on locked findings. */
export const LOCKED_SENTINEL = 'LOCKED'

/** True when a backend value is the LOCKED sentinel rather than real evidence. */
export function isLockedValue(value: unknown): boolean {
  return typeof value === 'string' && value.trim() === LOCKED_SENTINEL
}

/**
 * Single source of truth for report access. Pro users receive the full
 * analysis while `is_unlocked` stays false, so both must be checked.
 * Scan credits never grant access — they only mean "can run another scan".
 */
export function hasFullReportAccess(
  isPro: boolean,
  scan: Pick<Scan, 'is_unlocked'> | null | undefined,
): boolean {
  return isPro === true || scan?.is_unlocked === true
}

export interface ReportFinding {
  pattern: string
  severity: string
  example: string
}

export interface ReportMessage {
  text?: string
  severity?: number
  flags?: string[]
  short_reason?: string
}

/**
 * One message count for every surface. Only the real `message_count` column
 * counts — nothing is derived. Returns null otherwise so the UI says
 * "Conversation analyzed" and never "0 Messages".
 */
export function getMessageCount(
  scan: Pick<Scan, 'message_count'> | null | undefined,
): number | null {
  if (!scan) return null
  return typeof scan.message_count === 'number' && scan.message_count > 0
    ? scan.message_count
    : null
}

/** "11 Messages Analyzed" or "Conversation analyzed". */
export function formatMessagesAnalyzed(count: number | null): string {
  if (!count) return 'Conversation analyzed'
  return `${count} ${count === 1 ? 'Message' : 'Messages'} Analyzed`
}

/** Compact form for lists: "11 messages" or "Conversation analyzed". */
export function formatMessageCountShort(count: number | null): string {
  if (!count) return 'Conversation analyzed'
  return `${count} ${count === 1 ? 'message' : 'messages'}`
}

export function resolveRiskLevel(
  scan: Pick<Scan, 'risk_level' | 'score' | 'analysis'>,
): RiskLevel {
  const fromScan = scan.risk_level
  if (fromScan === 'low' || fromScan === 'medium' || fromScan === 'high') {
    return fromScan
  }
  const fromAnalysis = scan.analysis?.risk_level
  if (
    fromAnalysis === 'low' ||
    fromAnalysis === 'medium' ||
    fromAnalysis === 'high'
  ) {
    return fromAnalysis
  }
  const score = scan.score || 0
  return score >= 70 ? 'high' : score >= 40 ? 'medium' : 'low'
}

function normalizeSeverity(value: unknown): Severity {
  const upper = String(value || '').toUpperCase()
  if (upper === 'HIGH' || upper === 'LOW') return upper
  return 'MEDIUM'
}

export function normalizePatterns(
  patterns: Scan['patterns'],
): NormalizedPattern[] {
  if (!Array.isArray(patterns)) return []
  return patterns.map((pattern) => {
    if (typeof pattern === 'string') {
      return {
        type: pattern,
        severity: 'MEDIUM',
        description: null,
        signalCount: null,
        examples: [],
        evidenceLocked: false,
      }
    }
    const rawExamples = Array.isArray(pattern.examples) ? pattern.examples : []
    const singleExample = (pattern as { example?: unknown }).example
    const evidenceLocked =
      isLockedValue(singleExample) || rawExamples.some(isLockedValue)
    // The LOCKED sentinel is a lock marker, never evidence text.
    const examples = rawExamples.filter(
      (ex) => typeof ex === 'string' && ex.trim() && !isLockedValue(ex),
    )
    const count =
      typeof pattern.count === 'number' && pattern.count > 0
        ? pattern.count
        : examples.length > 0
          ? examples.length
          : null
    return {
      type: pattern.type || 'Unclassified pattern',
      severity: normalizeSeverity(pattern.severity),
      description: pattern.description || null,
      signalCount: count,
      examples,
      evidenceLocked,
    }
  })
}

export function getFindings(scan: Scan): ReportFinding[] {
  const findings = scan.analysis?.findings
  return Array.isArray(findings) ? findings : []
}

export function getReportMessages(scan: Scan): ReportMessage[] {
  const messages = scan.analysis?.messages
  return Array.isArray(messages) ? messages : []
}

export function getConversationText(scan: Scan): string {
  const text = (scan as Scan & { conversation_text?: string | null })
    .conversation_text
  return typeof text === 'string' ? text.trim() : ''
}

/** Personalized recommendations from the analyzer, else risk-level guidance. */
export function getReportRecommendations(scan: Scan): string[] {
  const recs = scan.analysis?.recommendations
  if (Array.isArray(recs) && recs.length > 0) {
    return recs.filter((rec) => typeof rec === 'string' && rec.trim())
  }
  return getFallbackRecommendations(resolveRiskLevel(scan))
}

export function getPatternSentence(count: number): string {
  if (count === 0) {
    return 'No significant communication patterns were detected in this conversation.'
  }
  return `${count} significant communication ${count === 1 ? 'pattern was' : 'patterns were'} detected in this conversation.`
}

export function formatSignalCount(count: number | null): string {
  if (!count) return 'Evidence detected'
  return `${count} evidence ${count === 1 ? 'signal' : 'signals'} detected`
}
